import { computeCalendarRange } from './calendarRangeUtils';
import { getArchivedJobTimelineDate, getArchivedJobLocalDayKey, parseMoneyAmountSR } from './displayUtils';
import { resolveTeamMembers, getWorkerMaxDailyInterventions, isJobAssignedToTeam, isJobAssignedToWorker } from './workerUtils';
import { calculateWorkerRating } from './workerRatingUtils';
import { isWorkerAvailableForWork, isTeamAvailableForWork } from './workerAvailabilityUtils';

/** Isti završeni statusi kao u arhivi (bez otkazanih). */
export const WORKER_COMPLETED_STATUSES = ['Completed', 'Finished', 'Archived'];

export const isWorkerCompletedJob = (job) =>
  WORKER_COMPLETED_STATUSES.includes(job?.status);

const anchorNow = () => new Date();

export const getStatsPeriodRange = (period, anchorDate = anchorNow()) => {
  const d = new Date(anchorDate);

  if (period === 'quarter') {
    const qStartMonth = Math.floor(d.getMonth() / 3) * 3;
    const start = new Date(d.getFullYear(), qStartMonth, 1, 0, 0, 0, 0);
    const end = new Date(d.getFullYear(), qStartMonth + 3, 0, 23, 59, 59, 999);
    return { start, end };
  }

  const mode = period === 'week' ? 'week' : 'month';
  const { startDate, endDate } = computeCalendarRange(d, mode);
  return { start: new Date(startDate), end: new Date(endDate) };
};

export const getPreviousStatsPeriodRange = (period, anchorDate = anchorNow()) => {
  const d = new Date(anchorDate);

  if (period === 'week') {
    const prev = new Date(d);
    prev.setDate(prev.getDate() - 7);
    return getStatsPeriodRange('week', prev);
  }

  if (period === 'month') {
    const prev = new Date(d.getFullYear(), d.getMonth() - 1, 15);
    return getStatsPeriodRange('month', prev);
  }

  if (period === 'quarter') {
    const prev = new Date(d.getFullYear(), d.getMonth() - 3, 15);
    return getStatsPeriodRange('quarter', prev);
  }

  return getStatsPeriodRange('month', new Date(d.getFullYear(), d.getMonth() - 1, 15));
};

/** ISO opseg za fetchArchiveJobs — usklađen sa getStatsPeriodRange. */
export const getArchiveFetchRangeForStatsPeriod = (period, anchorDate = anchorNow()) => {
  const { start, end } = getStatsPeriodRange(period, anchorDate);
  return {
    start,
    end,
    startDate: start.toISOString(),
    endDate: end.toISOString()
  };
};

export const isJobInStatsPeriod = (job, start, end) => {
  const completedDate = getArchivedJobTimelineDate(job);
  if (Number.isNaN(completedDate.getTime())) return false;
  return completedDate >= start && completedDate <= end;
};

export const filterJobsForWorker = (jobs, worker, teams = []) =>
  (jobs || []).filter((job) => isJobAssignedToWorker(job, worker._id, teams));

export const getWorkerCompletedJobsInPeriod = (jobs, worker, start, end, teams = []) =>
  filterJobsForWorker(jobs, worker, teams).filter(
    (job) => isWorkerCompletedJob(job) && isJobInStatsPeriod(job, start, end)
  );

export const categorizeVisitJobs = (jobs) => {
  const firstVisitJobs = jobs.filter(
    (job) => !job.isReturnVisit && (job.visitCount === 1 || !job.visitCount)
  );
  const secondVisitJobs = jobs.filter(
    (job) => job.isReturnVisit || (job.visitCount && job.visitCount > 1)
  );
  return { firstVisitJobs, secondVisitJobs };
};

export const formatMonthKey = (year, monthIndex) =>
  `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

export const parseMonthKey = (monthKey) => {
  const [year, month] = String(monthKey || '').split('-').map(Number);
  if (!year || !month) {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  }
  return { year, month: month - 1 };
};

export const getMonthRange = (year, monthIndex) => {
  const start = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const end = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
  return { start, end };
};

export const shiftMonthKey = (monthKey, delta) => {
  const { year, month } = parseMonthKey(monthKey);
  const d = new Date(year, month + delta, 1);
  return formatMonthKey(d.getFullYear(), d.getMonth());
};

export const formatMonthLabel = (monthKey, locale = 'sr-RS') => {
  const { year, month } = parseMonthKey(monthKey);
  const label = new Date(year, month, 1).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric'
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const getWorkerMonthMaxCapacity = (worker, year, monthIndex) => {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  let workDays = 0;
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, monthIndex, day);
    if (isWorkerAvailableForWork(worker, date)) workDays += 1;
  }
  return workDays * getWorkerMaxDailyInterventions(worker);
};

const normalizeAddress = (address) =>
  String(address || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

const parseJobRevenue = (job) => {
  const raw = job?.report?.serviceCharged ?? job?.report?.serviceChargedAmount ?? 0;
  if (typeof raw === 'number') return raw;
  return parseMoneyAmountSR(String(raw));
};

const parseJobExpenses = (job) => {
  const report = job?.report;
  if (!report) return 0;
  if (typeof report.totalCost === 'number') return report.totalCost;
  const parts =
    (Number(report.reversiCost) || 0) +
    (Number(report.additionalMaterialsCost) || 0) +
    (Number(report.amortizationCost) || 0);
  return parts;
};

/** Mesečna statistika radnika iz arhive (bez valute — samo brojevi). */

/** Samo poslovi eksplicitno dodeljeni timu (team:id) — zajednički rad tima. */
export const filterJobsForTeam = (jobs, team) =>
  (jobs || []).filter((job) => isJobAssignedToTeam(job, team._id));

/** Kapacitet tima = zbir dnevnog kapaciteta članova samo na danima kada je tim dostupan. */
export const getTeamMonthMaxCapacity = (team, workers, year, monthIndex) => {
  const members = resolveTeamMembers(team, workers);
  if (members.length === 0) return 0;

  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  let capacity = 0;

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, monthIndex, day);
    if (!isTeamAvailableForWork(team, workers, date)) continue;
    capacity += members.reduce(
      (sum, member) => sum + getWorkerMaxDailyInterventions(member),
      0
    );
  }

  return capacity;
};

export const getTeamCompletedJobsInPeriod = (jobs, team, start, end) =>
  filterJobsForTeam(jobs, team).filter(
    (job) => isWorkerCompletedJob(job) && isJobInStatsPeriod(job, start, end)
  );

/** Mesečna statistika tima — samo zajednički poslovi dodeljeni timu. */
export const computeTeamMonthStats = (team, workers, archiveJobs, monthKey) => {
  const { year, month } = parseMonthKey(monthKey);
  const { start, end } = getMonthRange(year, month);
  const members = resolveTeamMembers(team, workers);

  const completedInPeriod = getTeamCompletedJobsInPeriod(archiveJobs, team, start, end);
  const { firstVisitJobs, secondVisitJobs } = categorizeVisitJobs(completedInPeriod);

  let totalRevenue = 0;
  let totalExpenses = 0;
  let complaintsCount = 0;
  const addressSet = new Set();
  const daySet = new Set();

  completedInPeriod.forEach((job) => {
    if (job.isReturnVisit || job.complaint) complaintsCount += 1;

    const addr = normalizeAddress(job.clientAddress);
    if (addr) addressSet.add(addr);

    const dayKey = getArchivedJobLocalDayKey(job);
    if (dayKey) daySet.add(dayKey);

    if (job.report) {
      totalRevenue += parseJobRevenue(job);
      totalExpenses += parseJobExpenses(job);
    }
  });

  const monthMaxCapacity = getTeamMonthMaxCapacity(team, workers, year, month);
  const avgCoefficient =
    members.length > 0
      ? members.reduce((sum, m) => sum + (m.specializationCoefficient || 1), 0) / members.length
      : 1;

  return {
    monthKey,
    monthLabel: formatMonthLabel(monthKey),
    firstVisitJobs: firstVisitJobs.length,
    secondVisitJobs: secondVisitJobs.length,
    completedJobs: completedInPeriod.length,
    complaintsCount,
    totalRevenue,
    totalExpenses,
    uniqueAddresses: addressSet.size,
    activeDays: daySet.size,
    monthMaxCapacity,
    coefficient: avgCoefficient,
    memberCount: members.length
  };
};

/** Rejting tima iz zajedničkih poslova dodeljenih timu. */
export const calculateTeamRating = (team, workers, archiveJobs, monthKey, ratingParams) => {
  const stats = computeTeamMonthStats(team, workers, archiveJobs, monthKey);
  if (stats.completedJobs === 0) {
    return { score: 0 };
  }
  const { score } = calculateWorkerRating(
    stats,
    { specializationCoefficient: stats.coefficient },
    ratingParams
  );
  return { score };
};

export const computeWorkerMonthStats = (worker, archiveJobs, monthKey, teams = []) => {
  const { year, month } = parseMonthKey(monthKey);
  const { start, end } = getMonthRange(year, month);

  const completedInPeriod = getWorkerCompletedJobsInPeriod(
    archiveJobs,
    worker,
    start,
    end,
    teams
  );

  const { firstVisitJobs, secondVisitJobs } = categorizeVisitJobs(completedInPeriod);

  let totalRevenue = 0;
  let totalExpenses = 0;
  let complaintsCount = 0;
  const addressSet = new Set();
  const daySet = new Set();

  completedInPeriod.forEach((job) => {
    if (job.isReturnVisit || job.complaint) complaintsCount += 1;

    const addr = normalizeAddress(job.clientAddress);
    if (addr) addressSet.add(addr);

    const dayKey = getArchivedJobLocalDayKey(job);
    if (dayKey) daySet.add(dayKey);

    if (job.report) {
      totalRevenue += parseJobRevenue(job);
      totalExpenses += parseJobExpenses(job);
    }
  });

  const monthMaxCapacity = getWorkerMonthMaxCapacity(worker, year, month);

  return {
    monthKey,
    monthLabel: formatMonthLabel(monthKey),
    firstVisitJobs: firstVisitJobs.length,
    secondVisitJobs: secondVisitJobs.length,
    completedJobs: completedInPeriod.length,
    complaintsCount,
    totalRevenue,
    totalExpenses,
    uniqueAddresses: addressSet.size,
    activeDays: daySet.size,
    monthMaxCapacity,
    coefficient: worker?.specializationCoefficient || 1
  };
};
